import React, { useState, useEffect, useMemo } from "react";
import { NavLink } from "react-router-dom";
import {
    FiCheckCircle,
    FiChevronDown,
    FiChevronLeft,
    FiChevronRight,
    FiChevronUp,
    FiEye,
    FiFileText,
    FiHome,
    FiPlusCircle,
    FiSearch,
    FiTrash2,
    FiUsers,
} from "react-icons/fi";
import { MdBadge, MdCancel, MdHourglassEmpty, MdStorefront, MdVerified } from "react-icons/md";
import { RoutesPaths, statusOptions } from "../Router/constant";
import { getPartners, deletePartnerById } from "../Services/partnerService";
import ConfirmationModal from "../Components/ConfirmationModal";
import { toast } from "react-toastify";
import Skeleton from "../Components/SkeletonLoader";

export default function Partners() {
    const [loading, setLoading] = useState(true);
    const [partnerData, setPartnerData] = useState([]);
    const [offset, setOffset] = useState(1);
    const [limit] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [status, setStatus] = useState("");
    const [confirmation, setConfirmation] = useState({ show: false, userId: null });
    const [deleteReason, setDeleteReason] = useState("");
    const [allData, setAllData] = useState({});
    const [search, setSearch] = useState("");
    // Sorts the rows of the current page; the API has no sort param.
    const [sort, setSort] = useState({ key: null, dir: "asc" });

    useEffect(() => {
        fetchAllData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset, status, search]);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const response = await getPartners({ offset, limit, status, search });
            const partners = response?.data?.data || [];
            const allData = response?.data || {};
            setPartnerData(partners);
            setAllData(allData?.statusCounts);
            setTotalCount(allData?.count || 0);
        } catch (error) {
            console.error("Error fetching partners:", error);
            setPartnerData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (userId) => {
        setConfirmation({ show: true, userId });
        setDeleteReason("");
    };

    const handleConfirmDelete = async () => {
        try {
            if (!deleteReason.trim()) {
                toast.error("Please provide a reason for deletion.");
                return;
            }

            if (confirmation.userId) {
                const response = await deletePartnerById(confirmation.userId, { message: deleteReason });

                if (response.data?.status === true || response.data?.status === 1) {
                    toast.success("Vendor deleted successfully.");
                    fetchAllData();
                    setConfirmation({ show: false, userId: null });
                    setDeleteReason("");
                } else {
                    toast.error(response.data?.message || "Failed to delete vendor.");
                }
            }
        } catch (error) {
            console.error("Error deleting partner:", error);
            toast.error("Something went wrong while deleting vendor.");
        }
    };

    const handleCancelDelete = () => {
        setConfirmation({ show: false, userId: null });
        setDeleteReason("");
    };

    const generatePagination = (currentPage, totalPages) => {
        const pages = [];

        if (totalPages <= 4) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (currentPage <= 2) {
                pages.push(1, 2, '...', totalPages - 1, totalPages);
            } else if (currentPage <= 4) {
                pages.push(3, 4, '...', totalPages - 1, totalPages);
            } else if (currentPage >= totalPages - 2) {
                for (let i = totalPages - 2; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(currentPage, currentPage + 1, '...', totalPages - 1, totalPages);
            }
        }

        return pages;
    };

    const rows = useMemo(() => {
        if (!sort.key) return partnerData;
        const value = (p) => String((sort.key === "status" ? p.status : p.companyName) || "").toLowerCase();
        return [...partnerData].sort((a, b) => {
            const diff = value(a).localeCompare(value(b));
            return sort.dir === "asc" ? diff : -diff;
        });
    }, [partnerData, sort]);

    const toggleSort = (key) =>
        setSort((prev) => ({
            key,
            dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
        }));

    const SortArrows = ({ column }) => (
        <span className="qb-sort-arrows">
            <FiChevronUp className={sort.key === column && sort.dir === "asc" ? "on" : ""} />
            <FiChevronDown className={sort.key === column && sort.dir === "desc" ? "on" : ""} />
        </span>
    );

    const summaryCards = [
        { label: "Total vendors", key: "total", tone: "orange", Icon: FiUsers, Art: MdBadge },
        { label: "Pending vendors", key: "pending", tone: "amber", Icon: MdHourglassEmpty, Art: MdHourglassEmpty },
        { label: "Rejected vendors", key: "rejected", tone: "rose", Icon: FiFileText, Art: MdCancel },
        { label: "Accepted vendors", key: "accepted", tone: "green", Icon: FiCheckCircle, Art: MdVerified },
    ];

    const totalPages = Math.ceil(totalCount / limit);
    const firstRow = totalCount === 0 ? 0 : (offset - 1) * limit + 1;
    const lastRow = Math.min(offset * limit, totalCount);

    return (
        <div className="container-fluid qd-page">
            {/* Breadcrumb */}
            <nav className="qd-breadcrumb">
                <NavLink to={RoutesPaths.DASHBOARD} aria-label="Dashboard">
                    <FiHome />
                </NavLink>
                <FiChevronRight />
                <span className="qd-breadcrumb-current">Vendors</span>
            </nav>

            {/* Summary cards */}
            <div className="qd-summary-grid qb-summary-grid">
                {summaryCards.map(({ label, key, tone, Icon, Art }) => {
                    const count =
                        key === "total"
                            ? (allData?.pending || 0) + (allData?.rejected || 0) + (allData?.accepted || 0)
                            : allData?.[key] || 0;

                    return (
                        <div className={`qd-card qd-summary qd-summary-${tone} qc-summary`} key={label}>
                            <div className="qd-stat-body">
                                <span className={`qd-stat-icon qd-tone-${tone} qc-summary-icon`}>
                                    <Icon />
                                </span>
                                {loading ? (
                                    <>
                                        <div className="qd-skel" style={{ width: 50, height: 32, marginBottom: 6 }} />
                                        <div className="qd-skel" style={{ width: 110, height: 16 }} />
                                    </>
                                ) : (
                                    <>
                                        <h3 className="qb-summary-value">{count}</h3>
                                        <p className="qb-summary-label">{label}</p>
                                    </>
                                )}
                            </div>
                            <span className="qb-summary-art" aria-hidden="true">
                                <Art />
                            </span>
                        </div>
                    );
                })}
            </div>

            {/* Vendor table */}
            <div className="qd-card qd-panel">
                <div className="qd-panel-header">
                    <div className="qd-panel-title">
                        <span className="qd-panel-icon">
                            <MdStorefront />
                        </span>
                        <div>
                            <h6>Vendors</h6>
                            <p className="qd-panel-sub">Manage all vendor registrations and their status.</p>
                        </div>
                    </div>
                    <div className="qd-bookings-tools">
                        <select
                            className="qd-select qd-select-wide"
                            value={status}
                            onChange={(e) => {
                                setStatus(e.target.value);
                                setOffset(1);
                            }}
                            aria-label="Filter by status"
                        >
                            {statusOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        <label className="qd-search qc-search">
                            <FiSearch />
                            <input
                                type="text"
                                placeholder="Search company name or tax id..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setOffset(1);
                                }}
                            />
                        </label>
                        <NavLink to={RoutesPaths.ADDVENDOR} className="qd-btn qd-btn-primary">
                            <FiPlusCircle /> Create vendor
                        </NavLink>
                    </div>
                </div>

                <div className="qd-table-wrap">
                    <table className="qd-table qp-table">
                        <thead>
                            <tr>
                                <th>
                                    <button type="button" className="qb-sort" onClick={() => toggleSort("company")}>
                                        Company name <SortArrows column="company" />
                                    </button>
                                </th>
                                <th>Registration / Tax id</th>
                                <th>Phone number</th>
                                <th>Year of experience</th>
                                <th>
                                    <button type="button" className="qb-sort" onClick={() => toggleSort("status")}>
                                        Status <SortArrows column="status" />
                                    </button>
                                </th>
                                <th className="qb-th-actions">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                [...Array(5)].map((_, i) => (
                                    <tr key={i}>
                                        <td><Skeleton width={140} /></td>
                                        <td><Skeleton width={130} /></td>
                                        <td><Skeleton width={100} /></td>
                                        <td><Skeleton width={40} /></td>
                                        <td><Skeleton width={90} height={24} /></td>
                                        <td><Skeleton width={180} height={32} /></td>
                                    </tr>
                                ))
                            ) : rows.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="qd-empty">No vendors found</td>
                                </tr>
                            ) : (
                                rows.map((user) => {
                                    const statusKey = String(user.status || "").toLowerCase();
                                    return (
                                        <tr key={user._id}>
                                            <td>
                                                <span className="qd-customer">
                                                    <span className="qd-avatar">
                                                        {(user.companyName || "?").trim().charAt(0).toUpperCase()}
                                                    </span>
                                                    <strong>{user.companyName}</strong>
                                                </span>
                                            </td>
                                            <td className="qd-mono">{user.businessRegistrationNumber || "N/A"}</td>
                                            <td>{user.phone}</td>
                                            <td>{user.yearOfExperience || 0}</td>
                                            <td>
                                                <span className={`qc-status qp-status-${statusKey}`}>
                                                    <span className="qu-dot" />
                                                    <span className="text-capitalize">{user.status || "N/A"}</span>
                                                </span>
                                            </td>
                                            <td>
                                                <div className="qd-actions qb-actions">
                                                    <NavLink
                                                        to={`${RoutesPaths.PARTNERSDETAIL}/${user._id}`}
                                                        className="qd-btn qd-btn-outline qd-btn-sm"
                                                    >
                                                        <FiEye /> View
                                                    </NavLink>
                                                    <button
                                                        type="button"
                                                        className="qd-btn qd-btn-sm qp-delete-btn"
                                                        onClick={() => handleDeleteClick(user._id)}
                                                    >
                                                        <FiTrash2 /> Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {!loading && totalCount > 0 && (
                    <div className="qd-pagination">
                        <span className="qd-muted">
                            Showing {firstRow} to {lastRow} of {totalCount} vendors
                        </span>
                        <div className="qd-pages">
                            <button disabled={offset <= 1} onClick={() => setOffset(offset - 1)} aria-label="Previous">
                                <FiChevronLeft />
                            </button>
                            {generatePagination(offset, totalPages).map((page, idx) =>
                                page === '...' ? (
                                    <span key={idx} className="qd-ellipsis">...</span>
                                ) : (
                                    <button
                                        key={idx}
                                        className={offset === page ? "active" : ""}
                                        onClick={() => setOffset(page)}
                                    >
                                        {page}
                                    </button>
                                )
                            )}
                            <button disabled={offset >= totalPages} onClick={() => setOffset(offset + 1)} aria-label="Next">
                                <FiChevronRight />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                show={confirmation.show}
                title="Confirm delete"
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                confirmText="Yes, delete"
                cancelText="Cancel"
            >
                <p>Do you really want to delete this vendor?</p>
                <textarea
                    className="qb-modal-input"
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder="Enter reason for deletion..."
                    rows="4"
                />
            </ConfirmationModal>
        </div>
    );
}
